<template>
  <div>
    <div class="tb-expand">
      <span class="expd-open" @click="handleTableExpand">
        <font-awesome-icon
          :icon="[
            'far',
            `${
              isTableExpanded
                ? 'arrow-down-left-and-arrow-up-right-to-center'
                : 'arrow-up-right-and-arrow-down-left-from-center'
            }`,
          ]"
          class="exd-icon"
        />
      </span>
    </div>
    <div class="pnl_title_right">
      <div class="pnl_top_icon_wrap">
        <!-- <div class="pnl_top_icon" @click="pageExportToPDF('performanceAnalysis_container')"> -->
        <div class="pnl_top_icon" @click="handleExportClick">
          <font-awesome-icon :icon="['fas', 'arrow-down-to-line']" />
        </div>
      </div>
    </div>
    <div class="pnl_itm_title">
      <div v-if="!hasTabs" class="pnl_itm_title_txt active">{{ sectionTitle }}</div>
      <template v-else>
        <div
          v-for="(tab, i) in tabOptions"
          :key="i"
          class="pnl_itm_title_txt"
          :class="{ active: tab.id === selectedTab }"
          @click="onTabClick(tab)"
        >
          {{ tab.value }}
        </div>
      </template>
    </div>
    <div
      ref="table-scroll"
      class="panel_scroll_wrap table-scroll"
      :class="isTableExpanded ? 'reactive-ht' : 'height270'"
      style="overflow-x: scroll;"
    >
      <div v-if="!loading && rows.length" class="tg-wrap">
        <table id="tg-wqhPh" class="tg hm_table">
          <thead>
            <tr>
              <th
                v-for="(col, j) in newMetaData"
                :key="j"
                :ref="`${j}_newMetaData`"
                class="tg-ul38 tg-sort-header th-title-tt"
                :class="{
                  firstCol: j === 0,
                  firstColExpanded: j === 0 && isTableExpanded,
                  rt: col.align === 'right',
                  ctr: col.align === 'center',
                  'tg-sort-asc': sortBy === col.key && sortDirection === 'desc',
                  'tg-sort-desc': sortBy === col.key && sortDirection === 'asc',
                  sticky: j < stickyColumnCount, // Apply sticky class dynamically
                  'last-sticky': j === stickyColumnCount - 1,
                }"
                :style="
                  j < stickyColumnCount
                    ? {
                        left: `${j === 0 ? 0 : 274 + (j - 1) * 100}px`,
                        minWidth: j === 0 ? '274px' : '100px',
                        maxWidth: j === 0 ? '274px' : 'auto',
                      }
                    : {}
                "
                @click="(e) => clickHandler(e, col)"
              >
                {{ col.value }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in sortedRows"
              :id="i"
              :key="i"
              :style="{ position: 'relative' }"
              :class="{ rowhover: i === hoveredRowIndex }"
              @mouseover="handleMouseOverRow(i)"
              @mouseleave="handleMouseLeaveRow"
            >
              <td
                v-for="(col, j) in newMetaData"
                :key="j"
                :ref="`${i}_${j}_newMetaData`"
                class="tg-0lax"
                :class="{
                  firstCol: j === 0,
                  firstColExpanded: j === 0 && isTableExpanded,
                  rt: col.align === 'right',
                  sticky: j < stickyColumnCount, // Apply sticky class dynamically
                  'last-sticky': j === stickyColumnCount - 1,
                }"
                :style="
                  j < stickyColumnCount
                    ? {
                        left: `${j === 0 ? 0 : 274 + (j - 1) * 100}px`,
                        minWidth: j === 0 ? '274px' : '100px',
                        maxWidth: j === 0 ? '274px' : 'auto',
                      }
                    : {}
                "
              >
                <pacing-chart
                  v-if="col.formatter && col.formatter.type === 'pacing'"
                  :key="row[col.key]"
                  :pacing="Math.round(Number(row[col.key]))"
                  :row-data="row"
                />
                <div
                  v-else-if="col.formatter && col.formatter.type === 'status'"
                  :key="row[col.key]"
                  class="tgs-status"
                  :class="`tgs-${row[col.key]?.toLowerCase()}`"
                >
                  {{ row[col.key] }}
                </div>
                <template v-else>
                  {{ formattedColumnValue(row, col) }}
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <b-tooltip
        :tooltip-text="`${activeEl.text}`"
        :visible-style="{ ...activeEl.style, zIndex: 9999 }"
      />
      <div
        v-if="!loading && !rows.length"
        class="txt-center"
        style="margin-top: 13px;font-size: 14px !important;"
      >
        No data is available to show
      </div>
      <div class="spinner">
        <b-loading-spinner v-if="loading" class="txt-center" />
      </div>
    </div>
  </div>
</template>
<script>
import numeral from 'numeral';
import { uuid } from '@/helpers/global/misc-helpers';
import { isBlank } from 'adready-api/helpers/common';
import { pageExportToPDF, isFormattedValueZero } from '@/util/utility-functions';
import PacingChart from '~/components/elements/pacing-chart.vue';
import { formatDateInEST } from '~/util/apiDateFormat';

import BTooltip from '~/components/elements/b-tooltip.vue';

export default {
  components: {
    PacingChart,
    BLoadingSpinner: () =>
      import(
        /* webpackChunkName: "b-loading-spinner" */ '~/components/elements/b-loading-spinner.vue'
      ),
    BTooltip,
  },
  props: {
    stickyColumnCount: {
      type: Number,
      required: false,
      default: 1, // Default to sticking the first column
    },
    sectionTitle: {
      type: String,
      required: true,
    },
    rows: {
      type: Array,
      required: true,
    },
    metaData: {
      type: Array,
      required: true,
    },
    isBrandTable: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    tabOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    loading: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    isTableExpanded: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    sortBy: {
      type: String,
      required: false,
      default: () => '',
    },
    sortDirection: {
      type: String,
      required: false,
      default: () => 'asc',
    },
  },
  data() {
    let selectedTab = '';
    if (this.tabOptions?.length) {
      const defaultSelectedTab = this.tabOptions.find((t) => t.selected);
      selectedTab = defaultSelectedTab?.id || this.tabOptions[0].id;
    }
    return {
      selectedTab,
      activeEl: { style: { display: 'none', visibility: 'hidden' }, text: '', index: -1 },
      tooltipId: uuid(),
      hoveredRowIndex: -1,
      pageExportToPDF,
    };
  },
  computed: {
    newMetaData() {
      const { metaData } = this._props;
      let newMetaData = metaData;
      newMetaData = newMetaData.filter((item) => item.key !== 'status');
      return newMetaData;
    },
    hasTabs() {
      return this.tabOptions.length;
    },

    sortedRows() {
      const data = [...this.rows];
      return data.sort((a, b) => {
        let result = 1;

        if (this.sortDirection === 'desc') {
          result = -1;
        }
        const aValue = a[this.sortBy] === 'NA' ? 0 : a[this.sortBy];
        const bValue = b[this.sortBy] === 'NA' ? 0 : b[this.sortBy];
        if (aValue < bValue) {
          return -1 * result;
        }
        if (aValue > bValue) {
          return 1 * result;
        }
        return 0;
      });
    },
  },
  watch: {
    tabOptions(newTabOptions) {
      this.selectedTab = '';
      if (newTabOptions?.length) {
        const defaultSelectedTab = newTabOptions.find((t) => t.selected);
        this.selectedTab = defaultSelectedTab?.id || newTabOptions[0].id;
      }
    },
  },
  methods: {
    handleMouseOverRow(rowIndex) {
      this.hoveredRowIndex = rowIndex; // Update the 'hoveredRowIndex' when the row is hovered
    },

    handleMouseLeaveRow() {
      this.hoveredRowIndex = -1; // Reset the 'hoveredRowIndex' when the mouse leaves the row
    },
    handleMouseOver(el, text, refKey, isShowTooltip = true, isHeading = false) {
      if ((!isShowTooltip || text.length < 19) && text !== 'Pacing') {
        return;
      }
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const elementPos = this.$refs[refKey][0];
      let tableElement;
      if (isSafari) {
        tableElement = this.$refs['table-scroll'];
      } else {
        tableElement = document.getElementsByClassName('table-scroll');
      }

      if (!elementPos || !tableElement) {
        return;
      }
      // if (!(elementPos.offsetWidth < elementPos.scrollWidth)) {
      //   return;
      // }
      const style = {
        display: 'block',
        opacity: '0.96',
        'max-width': '500px',
        width: 'max-content',
      };
      let scrollTopVal = isSafari ? tableElement.scrollTop : tableElement[0].scrollTop;
      if (isHeading) {
        scrollTopVal = isSafari ? 0 : 150;
        if (this.isBrandTable) {
          scrollTopVal = isSafari ? 0 : -45;
        }
      }
      if (isSafari) {
        style.top = `${elementPos.offsetTop - scrollTopVal - 10}px`;
        style.left = `${elementPos.offsetLeft - tableElement.scrollLeft + 70}px`;
        style.visibility = 'visible';
      } else {
        style.top = `${elementPos.offsetParent.offsetTop - scrollTopVal - 10}px`;
        style.left = `${elementPos.offsetLeft - tableElement[0].scrollLeft + 70}px`;
        style.visibility = 'visible';
      }

      if (text === 'Pacing') {
        text =
          'The pacing charts show where the campaign is currently based on the spend in relation to \nthe flight date range. The black line over the pacing bar is the expected pacing. The % \ndifference between the actual and expected pacing is shown under it, as what % under or \nover pacing.';
        style.wordWrap = 'normal';
        style.width = '200px';
        style.height = '130px';
        style.marginTop = '-160px';
        style.marginLeft = '-110px';
        style.lineHeight = '1.3em';
      }
      this.activeEl = {
        style,
        text,
      };
    },

    handleMouseLeave() {
      this.activeEl = { style: { display: 'none' }, text: '' };
    },
    getDimensionForExport() {
      let dimension = this.sectionTitle;
      if (this.hasTabs) {
        const tab = this.tabOptions.find((t) => t.id === this.selectedTab);
        dimension = tab.value || '';
      }
      return dimension;
    },
    handleExportClick() {
      const header = [];
      const lines = [];
      this.newMetaData.forEach((element) => {
        header.push(element.value);
      });
      this.sortedRows.forEach((row) => {
        const data = [];
        this.newMetaData.forEach((col) => {
          data.push(this.formattedColumnValue(row, col));
        });
        lines.push(data);
      });
      const csvData = [header, ...lines];
      const dimension = this.getDimensionForExport();
      const fileName = `export_${dimension}_performance.csv`;
      this.$emit('on-export', fileName, csvData);
    },
    formattedColumnValue(row, col) {
      let val = row[col.key];
      const { formatter } = col;
      if (val === undefined) {
        val = ['number', 'percentage', 'pacing'].includes(formatter?.type) ? 0 : '';
      }

      if (val !== undefined && formatter) {
        if (formatter.type === 'number') {
          const isValueInfinity = !isBlank(col.infinityKey) && row[col.infinityKey];
          val = isValueInfinity ? '--' : numeral(val).format(formatter.format);
          val = isFormattedValueZero(val) ? '--' : val;
        } else if (['percentage', 'pacing'].includes(formatter.type)) {
          const isValueInfinity = !isBlank(col.infinityKey) && row[col.infinityKey];
          val = isValueInfinity ? '--' : numeral(val / 100).format(formatter.format);
          val = isFormattedValueZero(val) ? '--' : val;
        } else if (formatter.type === 'datetime') {
          val = formatDateInEST(val, formatter.format);
        }
      }

      return val;
    },
    handleTableExpand() {
      if (!this.isTableExpanded) {
        const el = document.getElementById('performanceAnalysis');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        }
      } else {
        window.scrollBy({
          top: -50,
          behavior: 'smooth',
        });
      }
      this.$emit('on-table-expand', !this.isTableExpanded);
    },
    clickHandler(event, col) {
      event?.stopPropagation();
      event?.preventDefault();
      const colKey = col.key;
      if (this.sortBy === colKey) {
        this.$emit('on-sort-direction-change', this.sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        this.$emit('on-sort-direction-change', 'asc');
        this.$emit('on-change-sort-by', colKey);
      }
    },
    onTabClick(tab) {
      this.selectedTab = tab.id;
      this.$emit('on-tab-click', this.selectedTab, tab.value);
    },
  },
};
</script>
<style lang="scss" scoped>
.th-title-tt {
  max-width: 159px;
  min-width: 120px;
  overflow: hidden !important;
  text-overflow: ellipsis;
}
.pnl_itm_title {
  border-bottom: 1px solid #e0e5ee;
  padding-bottom: 0px;
  white-space: nowrap;
}

.pnl_itm_title_txt {
  display: inline-block;
  position: relative;
  border-bottom: 0px solid;
  padding-bottom: 15px;
  top: 2px;
  z-index: 9;
  color: var(--primarylight);
  font-size: 1.02em;
  font-weight: 600;
  margin-right: 25px;
  cursor: pointer;
}
.pnl_itm_title_txt:hover {
  color: var(--primarydark);
}
.pnl_itm_title_txt.active {
  display: inline-block;
  position: relative;
  border-bottom: 3px solid var(--primarycolor);
  padding-bottom: 15px;
  top: 2px;
  z-index: 9;
  font-weight: 600;
  color: var(--primarydark);
}

.pnl_title_right {
  display: inline-block;
  width: 250px;
  text-align: right;
  float: right;
  margin-top: -6px;
  position: relative;
}
.tb-expand {
  position: absolute;
  top: 4px;
  right: 10px;
  font-size: 11px;
  color: var(--primarylight);
  z-index: 9;
}

.tb-expand:hover {
  color: var(--primarycolor);
}

.pnl_top_icon_wrap {
  display: inline-block;
  position: relative;
  padding: 4px 0px;
}

.pnl_top_icon {
  display: inline-block;
  padding: 3px 7px;
  font-size: 16px;
  color: var(--primarylight);
}
.pnl_top_icon:hover {
  color: var(--primarycolor);
}
.panel_scroll_wrap {
  overflow-y: scroll;
  height: 100%;
  padding-right: 10px;
  overflow-x: hidden;
  &.table-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    & tr:hover {
      background-color: #f0f1f5;
    }
    & tr:nth-child(even) {
      box-shadow: inset 0px 0px 90px rgba(0, 0, 0, 0.04);
    }
  }
  &.height654 {
    height: 654px;
  }
  &.height270 {
    max-height: 270px;
    min-height: 48px;
  }
  &.reactive-ht {
    height: calc(100vh - 160px) !important;
    max-height: calc(100vh - 160px) !important;
  }
}

.hm_table {
  border: 0px solid #fff;
  width: 100%;
}
.hm_table tr {
  width: 100%;
  text-align: left;
  table-layout: fixed;
}
.hm_table {
  .firstCol {
    max-width: 274px;
    min-width: 274px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.tg {
  border-collapse: collapse;
  border-spacing: 0;
}
.tg td {
  border-color: var(--primarydark2);
  border-style: solid;
  border-width: 0px;
  font-size: 13px;
  font-size: 0.86em;
  padding: 13px 5px;
  word-break: normal;
  font-weight: 600;
  white-space: nowrap;
}
// .tg td:hover {
//   white-space: normal;
//   overflow: visible;
//   text-overflow: inherit;
// }
.tg th {
  border-color: black;
  border-style: solid;
  border-width: 0px;
  font-size: 12px;
  font-weight: 600;
  overflow: visible;
  padding: 17px 5px;
  word-break: normal;
  color: var(--primarylight);
  background-color: #fbfbfb;
  padding: 10px 5px;
  padding: 14px 5px;
  white-space: nowrap;
  width: 120px;
  z-index: 9;
}

.full-width .tg th {
  padding: 14px 30px 14px 15px;
  box-shadow: inset 0px -2px 1px -1px rgba(0, 0, 0, 0.1) !important;
}
.full-width .tg td {
  padding: 13px 30px 13px 15px;
}
/*.tg .tg-ul38{position:-webkit-sticky;position:sticky;text-align:left;top:-1px;vertical-align:top;will-change:transform;z-index: 2;}*/
.tg-ul38 {
  position: -webkit-sticky;
  position: sticky;
  text-align: left;
  top: -1px;
  vertical-align: top;
  will-change: transform;
  z-index: 2;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.3);
  box-shadow: inset 0px -6px 0px -5px #d4e1e8 !important;
  &.firstColExpanded {
    width: 274px;
  }
}
.tg-wrap tr:hover {
  background-color: #f0f1f5;
}
.tg-wrap tr:nth-child(even) {
  box-shadow: inset 0px 0px 90px rgba(0, 0, 0, 0.04);
}

.tg .tg-0lax {
  text-align: left;
  vertical-align: top;
}
.tg .tg-0lax.firstColExpanded {
  width: 274px;
}
.tg-sort-header::-moz-selection {
  background: 0 0;
}
.tg-sort-header::selection {
  background: 0 0;
}
.tg-sort-header {
  cursor: pointer;
}
.tg-sort-header:after {
  content: '';
  margin: 7px 0px 0px 5px;
  border-width: 0 5px 5px;
  border-style: solid;
  border-color: #404040 transparent;
  visibility: hidden;
  position: absolute;
}
.tg-sort-header:hover:after {
  visibility: visible;
}
.tg-sort-header.rt:after {
  right: 15px;
  bottom: 20px;
}
.tg-sort-asc:after,
.tg-sort-asc:hover:after,
.tg-sort-desc:after {
  visibility: visible;
  opacity: 0.4;
}
.tg-sort-desc:after {
  border-bottom: none;
  border-width: 5px 5px 0;
}

@media screen and (max-width: 767px) {
  .tg {
    width: auto !important;
  }
  .tg col {
    width: auto !important;
  }
  .tg-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.rt {
  text-align: right !important;
}
.ctr {
  text-align: center !important;
}
.lft {
  text-align: left !important;
}
.tg-0lax i {
  font-size: 14px;
}

.expd {
  height: calc(100vh - 90px) !important;
  max-height: calc(100vh - 90px) !important;
}

/*stick first column*/

/*.full-width .tg-wrap tr {position: relative;}*/
.full-width .tg td:first-child,
.full-width thead tr th:first-child {
  background-color: #fff;
  left: 0px;
  z-index: 3;
  position: sticky;
  box-shadow: inset -2px 0px 1px -1px rgba(0, 0, 0, 0.1);
}
.full-width .tg td.sticky,
.full-width thead tr th.sticky {
  background-color: #fff;
  z-index: 3;
  position: sticky;
  box-shadow: inset -2px 0px 1px -1px rgba(0, 0, 0, 0.1);
}
.full-width thead tr th.sticky {
  z-index: 10;
  background-color: #fbfbfb;
  box-shadow: inset -2px -2px 1px -1px rgba(0, 0, 0, 0.1) !important;
}
.full-width thead tr th:first-child {
  z-index: 10;
  background-color: #fbfbfb;
  box-shadow: inset -2px -2px 1px -1px rgba(0, 0, 0, 0.1) !important;
}
// this will add line after sticky column
.last-sticky {
  box-shadow: inset -2px 0px 1px -1px rgba(0, 0, 0, 0.1);
}
/* Handle alternate row coloring */
.full-width .tg tr:nth-child(even) td.sticky {
  background-color: #f9f9f9;
}

.full-width .tg tr:nth-child(odd) td.sticky {
  background-color: #fff;
}

.full-width .tg tr.rowhover td.sticky {
  background-color: #f0f1f5 !important;
}

.full-width .tg tr:nth-child(even) td:first-child {
  background-color: #f9f9f9; /* Alternate color for even rows */
}

.full-width .tg tr:nth-child(odd) td:first-child {
  background-color: #fff; /* Default color for odd rows */
}

.full-width .tg tr.rowhover td:first-child {
  background-color: #f0f1f5 !important;
}
.tgs-status {
  background-color: transparent;
  padding: 2px 9px 2px 7px;
  border-radius: 30px;
  border: 1px solid rgba(0, 0, 0, 0.07);
  display: inline-block;
  line-height: 1.2em;
  white-space: nowrap;
}

.tgs-status::before {
  background-color: #eee;
  content: '';
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 5px;
}

.tgs-live.tgs-status::before {
  background-color: #64ba45;
}
.tgs-pending.tgs-status::before {
  background-color: #f09c51;
}
.tgs-submitted.tgs-status::before {
  background-color: #f09c51;
}
.tgs-paused.tgs-status::before {
  background-color: #e6c336;
}
.tgs-ended.tgs-status::before {
  background-color: #81a1c2;
}
.tgs-canceled.tgs-status::before {
  background-color: #fd9807;
}
::v-deep .rowhover {
  background-color: #f0f1f5 !important;
}
.pacingHeader {
  display: inline-block;
}
.spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
